import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { ApiClientError, idempotencyKeyFor } from '@/lib/http-core';
import { orderQueryKey } from './useOrderQuery';

/**
 * QA-454 — 주문자·배송 정보 수정. 계약은 BE 전달 문서(BO-TO-BE-2026-10-21-order-info-edit.md) 제안안.
 *   PATCH /api/admin/v1/orders/{orderId}/order-info
 * 바뀐 칸만 보낸다(없는 키 = 그대로). 배송지는 shippingAddress 안에서 바뀐 칸만.
 */
export interface OrderInfoShippingPatch {
  receiverName?: string;
  countryCode?: string;
  /** QA-567 — "" 는 지움. BE order AdminShippingAddressPatch 에 city·stateProvince 반영 후 동작. */
  city?: string;
  stateProvince?: string;
  zipCode?: string;
  address1?: string;
  address2?: string;
  receiverMobile?: string;
}

export interface OrderInfoPatch {
  ordererName?: string;
  ordererEmail?: string;
  ordererMobile?: string;
  shippingAddress?: OrderInfoShippingPatch;
  /** 배송 메모 — 운송장에 함께 찍히는 값(신규). */
  deliveryMemo?: string;
}

/** BE 반영 전(엔드포인트 없음) — 코드 없는 404/405. */
export class OrderInfoUpdateNotReadyError extends Error {}

export const useOrderInfoUpdateMutation = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation<void, Error, OrderInfoPatch>({
    mutationFn: async (body) => {
      const path = `/api/admin/v1/orders/${encodeURIComponent(orderId)}/order-info`;
      const res = await adminFetch(path, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor('PATCH', path, body) },
        body: JSON.stringify(body),
      });
      if (res.status === 405) throw new OrderInfoUpdateNotReadyError('not ready');
      if (!res.ok) {
        const err = await errorFromResponse(res);
        // 라우트가 없으면 본문 없는 404(HTTP_404) 또는 게이트웨이 공통 404 — 주문이 없다는 BE 코드와 구분한다.
        const code = err instanceof ApiClientError ? String(err.code) : '';
        if (res.status === 404 && (code === '' || code.startsWith('HTTP_') || code === 'COMMON_NOT_FOUND')) {
          throw new OrderInfoUpdateNotReadyError('not ready');
        }
        throw err;
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: orderQueryKey(orderId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};
